import React from 'react';
import { API_URL } from '../config';

export type ImportErrorJson = {
  reason:
    | 'unrecognized-format'
    | 'missing-required-fields'
    | 'ambiguous-data'
    | 'insufficient-context'
    | 'data-quality-issue';
  details: string;
  suggestions?: string[];
};

export type ImportRowState = {
  rowId: string;
  rowIndex: number;
  status: string;
  workflowId: string | null;
  workflowName: string | null;
  errorMessage: string | null;
  errorJson: ImportErrorJson | null;
};

export type ImportStreamState = {
  status: 'idle' | 'connecting' | 'streaming' | 'completed' | 'error';
  importStatus: string | null;
  totalRows: number;
  processedRows: number;
  failedRows: number;
  completedRows: number;
  rows: Record<string, ImportRowState>;
  /** Workflow IDs created during this import session - for highlighting newly imported workflows */
  newWorkflowIds: Set<string>;
  /** Workflow IDs currently being built (status='generating') - for "Building" indicator */
  buildingWorkflowIds: Set<string>;
  /** Number of debug runs started after import completion */
  autoRunCount: number;
};

type ImportRowEvent = {
  importId: string;
  rowId: string;
  rowIndex: number;
  status: string;
  workflowId: string | null;
  workflowName: string | null;
  errorMessage: string | null;
  errorJson: ImportErrorJson | null;
};

type ImportProgressEvent = {
  importId: string;
  status: string;
  totalRows: number;
  processedRows: number;
  failedRows: number;
  completedRows: number;
  autoRunCount?: number | null;
};

const createInitialState = (): ImportStreamState => ({
  status: 'idle',
  importStatus: null,
  totalRows: 0,
  processedRows: 0,
  failedRows: 0,
  completedRows: 0,
  rows: {},
  newWorkflowIds: new Set(),
  buildingWorkflowIds: new Set(),
  autoRunCount: 0,
});

interface UseImportStreamOptions {
  /** Called when a workflow is created (completed row with workflowId) */
  onWorkflowCreated?: (workflowId: string, workflowName: string | null) => void;
  /** Called when import completes */
  onComplete?: (state: ImportStreamState) => void;
  /** Called when debug runs are started after import */
  onAutoRunStarted?: (count: number) => void;
}

export function useImportStream(importId: string | null, options?: UseImportStreamOptions) {
  const [state, setState] = React.useState<ImportStreamState>(createInitialState);
  const eventSourceRef = React.useRef<EventSource | null>(null);
  const optionsRef = React.useRef(options);
  optionsRef.current = options;

  React.useEffect(() => {
    if (!importId) {
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
      setState(createInitialState());
      return;
    }

    setState((prev) => ({ ...prev, status: 'connecting' }));

    const eventSource = new EventSource(`${API_URL}/workflows/imports/${importId}/stream`, {
      withCredentials: true,
    });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setState((prev) => ({
        ...prev,
        status: 'streaming',
      }));
    };

    const handleRowEvent = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as ImportRowEvent;
        setState((prev) => {
          const newState = {
            ...prev,
            rows: {
              ...prev.rows,
              [payload.rowId]: {
                rowId: payload.rowId,
                rowIndex: payload.rowIndex,
                status: payload.status,
                workflowId: payload.workflowId,
                workflowName: payload.workflowName,
                errorMessage: payload.errorMessage,
                errorJson: payload.errorJson ?? null,
              },
            },
          };

          // Track newly created workflows and building state
          if (payload.workflowId) {
            const newIds = new Set(prev.newWorkflowIds);
            const buildingIds = new Set(prev.buildingWorkflowIds);
            const isNew = !newIds.has(payload.workflowId);

            if (payload.status === 'generating') {
              // Workflow just created, still building
              newIds.add(payload.workflowId);
              buildingIds.add(payload.workflowId);
              newState.newWorkflowIds = newIds;
              newState.buildingWorkflowIds = buildingIds;

              // Call callback to refresh workflow list
              if (isNew) {
                optionsRef.current?.onWorkflowCreated?.(payload.workflowId, payload.workflowName);
              }
            } else if (payload.status === 'completed') {
              // Workflow finished building
              newIds.add(payload.workflowId);
              buildingIds.delete(payload.workflowId);
              newState.newWorkflowIds = newIds;
              newState.buildingWorkflowIds = buildingIds;
            }
          }

          return newState;
        });
      } catch (error) {
        console.error('Failed to parse import-row event', error);
      }
    };

    const handleProgressEvent = (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data) as ImportProgressEvent;
        const isComplete =
          payload.status === 'completed' || payload.status === 'completed_with_errors';

        setState((prev) => {
          const autoRunCount = payload.autoRunCount ?? prev.autoRunCount;
          const newState: ImportStreamState = {
            ...prev,
            importStatus: payload.status,
            totalRows: payload.totalRows,
            processedRows: payload.processedRows,
            failedRows: payload.failedRows,
            completedRows: payload.completedRows,
            status: isComplete ? 'completed' : prev.status,
            autoRunCount,
          };

          if (isComplete) {
            optionsRef.current?.onComplete?.(newState);
            if (autoRunCount > 0) {
              optionsRef.current?.onAutoRunStarted?.(autoRunCount);
            }
          }

          return newState;
        });

        // Close event source when complete
        if (isComplete) {
          eventSource.close();
          eventSourceRef.current = null;
        }
      } catch (error) {
        console.error('Failed to parse import-progress event', error);
      }
    };

    eventSource.addEventListener('import-row', handleRowEvent as EventListener);
    eventSource.addEventListener('import-progress', handleProgressEvent as EventListener);

    eventSource.onerror = (err) => {
      console.error('Import stream error', err);
      setState((prev) => ({ ...prev, status: 'error' }));
      eventSource.close();
      eventSourceRef.current = null;
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [importId]);

  const disconnect = React.useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    setState(createInitialState());
  }, []);

  return {
    ...state,
    isConnected: state.status === 'streaming',
    disconnect,
  };
}
